/**
 * 知识卡库加载
 *
 * 卡库本体在 `content/cards/`（内容运营的领地），这里只做三件事：
 * 1. 用 `@chuandeng/shared` 的 schema 校验（坏数据在启动时就暴露，而不是玩到一半崩）；
 * 2. 标出哪些卡可以上线（`isPublishable`）—— 未审核的 draft 卡在正式包体里不参与出题；
 * 3. 按关卡给出题池。
 *
 * ⚠️ 已知偏差（MVP 临时措施）：首批 60 张卡里目前只有 biology-001 通过人工审核，
 * 若严格只出不审核的卡，第 2–5 关会完全没有题目。因此开发环境
 * （`import.meta.env.DEV` 或设置 `VITE_ALLOW_DRAFT_CARDS=1`）允许抽到 draft 卡，
 * 但界面上会打「待审核」标记 —— M2 批量起草新卡时靠它先把内容跑起来。
 * 正式包体恒为关闭：首批 60 张卡已于 2026-10-15 全部审核通过，草稿卡不该出现在孩子面前。
 */

import {
  isPublishable,
  knowledgeCardSchema,
  type KnowledgeCard,
  type LevelConfig,
} from "@chuandeng/shared";

const modules = import.meta.glob("../../../../content/cards/*/*.json", {
  eager: true,
  import: "default",
}) as Record<string, unknown>;

function loadCards(): { cards: KnowledgeCard[]; problems: string[] } {
  const cards: KnowledgeCard[] = [];
  const problems: string[] = [];
  for (const [path, raw] of Object.entries(modules)) {
    const parsed = knowledgeCardSchema.safeParse(raw);
    if (!parsed.success) {
      const first = parsed.error.issues[0];
      problems.push(`${path}: ${first?.path.join(".")} ${first?.message}`);
      continue;
    }
    cards.push(parsed.data);
  }
  cards.sort((a, b) => a.id.localeCompare(b.id));
  return { cards, problems };
}

const loaded = loadCards();

/** 全部卡片（含 draft），按 id 排序 */
export const ALL_CARDS: readonly KnowledgeCard[] = loaded.cards;

/** schema 校验失败的卡（正常情况下应为空） */
export const CARD_PROBLEMS: readonly string[] = loaded.problems;

/** 通过人工审核、可以进包体的卡 */
export const PUBLISHABLE_CARDS: readonly KnowledgeCard[] = ALL_CARDS.filter(isPublishable);

const BY_ID = new Map(ALL_CARDS.map((card) => [card.id, card]));

/** 是否允许在出题时使用未审核的 draft 卡（仅非生产构建） */
export const ALLOW_DRAFT_CARDS: boolean =
  import.meta.env.DEV || import.meta.env["VITE_ALLOW_DRAFT_CARDS"] === "1";

export function cardById(id: string): KnowledgeCard | undefined {
  return BY_ID.get(id);
}

export interface QuizPool {
  cards: KnowledgeCard[];
  /** 题池里是否包含未审核的卡（界面上要打标记） */
  includesDraft: boolean;
}

/**
 * 某关的出题池。
 * 优先只用已审核的卡；只有在校验通过且允许兜底时，才把 draft 卡也纳入。
 */
export function quizPoolForLevel(level: LevelConfig, allowDraft = ALLOW_DRAFT_CARDS): QuizPool {
  const all = level.cardIds.map(cardById).filter((c): c is KnowledgeCard => Boolean(c));
  const approved = all.filter(isPublishable);
  if (approved.length > 0) return { cards: approved, includesDraft: false };
  return allowDraft
    ? { cards: all, includesDraft: all.length > 0 }
    : { cards: [], includesDraft: false };
}

/** 图鉴：本关技能树节点绑定的卡（用于「首次解锁弹出讲解」） */
export function codexCardForNode(cardId: string | undefined): KnowledgeCard | undefined {
  if (!cardId) return undefined;
  return BY_ID.get(cardId);
}

/** 已收集卡片的统计（章节选择页的图鉴进度） */
export function collectionStats(ownedIds: readonly string[]): {
  owned: number;
  total: number;
  publishable: number;
  percent: number;
} {
  const owned = new Set(ownedIds);
  const ownedCount = ALL_CARDS.filter((c) => owned.has(c.id)).length;
  return {
    owned: ownedCount,
    total: ALL_CARDS.length,
    publishable: PUBLISHABLE_CARDS.length,
    percent: ALL_CARDS.length === 0 ? 0 : Math.round((ownedCount / ALL_CARDS.length) * 100),
  };
}
